import React, { Fragment, useCallback, useEffect, useState, useMemo } from "react";
import {
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    Row,
    Button,
    Modal,
    ModalBody,
    ModalHeader,
    Badge,
    ModalFooter,
    Pagination,
    PaginationItem,
    PaginationLink,
    Form, FormGroup, Label, Input, Table
} from "reactstrap";
import Breadcrumb from "../common/breadcrumb";
import { getSyncHistory, getSyncHistoryStats } from "../../utils/apiCalls";
import Loader from "../../shared/Loader";
import { RefreshCw, CheckCircle, XCircle, Loader as LoaderIcon, Clock } from "react-feather";
import CountUp from "react-countup";
import { useSelector } from "react-redux";

const MenuLogs = () => {
    const userType = localStorage.getItem("userType");
    const merchantId = localStorage.getItem("MerchantId");
    const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
    const selectedMerchant = userType === "merchant" ? merchantId : reduxSelectedMerchant;

    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);
    const [stats, setStats] = useState(null);
    const [menuLogs, setMenuLogs] = useState([]);
    const [days, setDays] = useState(30);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const [filters, setFilters] = useState({
        merchantId: selectedMerchant || "",
        syncType: "",
        status: "",
        startDate: "",
        endDate: "",
        limit: 10
    });
    const [tempFilters, setTempFilters] = useState(filters);

    const displayStats = useMemo(() => {
        const result = {
            total: 0,
            success: 0,
            menuTotal: 0,
            branchesTotal: 0
        };

        if (stats?.syncCounts && Array.isArray(stats.syncCounts)) {
            stats.syncCounts.forEach((item) => {
                const count = parseInt(item.count) || 0;
                result.total += count;
                if (item.status === 'success') result.success += count;

                if (item.syncType === 'menu') {
                    result.menuTotal += count;
                } else if (item.syncType === 'branches') {
                    result.branchesTotal += count;
                }
            });
        }
        return result;
    }, [stats]);

    const fetchStats = useCallback(async () => {
        setIsLoading(true);
        try {
            const params = {};
            if (selectedMerchant && selectedMerchant !== 'all') {
                params.merchantId = selectedMerchant;
            }

            if (filters.startDate || filters.endDate) {
                if (filters.startDate) params.startDate = filters.startDate;
                if (filters.endDate) params.endDate = filters.endDate;
            } else {
                params.days = days;
            }

            if (filters.syncType) params.syncType = filters.syncType;
            if (filters.status) params.status = filters.status;

            const response = await getSyncHistoryStats(params);
            setStats(response);
        } catch (error) {
            console.error("Error fetching sync stats:", error);
        } finally {
            setIsLoading(false);
        }
    }, [selectedMerchant, days, filters]);

    const fetchData = useCallback(async (page = 1, appliedFilters = filters) => {
        setIsLoading(true);
        try {
            const params = {
                page,
                limit: appliedFilters.limit,
                merchantId: selectedMerchant && selectedMerchant !== 'all' ? selectedMerchant : undefined,
                syncType: appliedFilters.syncType || undefined,
                status: appliedFilters.status || undefined,
                startDate: appliedFilters.startDate || undefined,
                endDate: appliedFilters.endDate || undefined,
            };
            const response = await getSyncHistory(params);

            console.log("response", response)
            setMenuLogs(response?.data?.records || []);
            setTotalPages(response?.data?.pagination?.totalPages || 1);
        } catch (error) {
            console.error("Error fetching menu sync logs:", error);
        } finally {
            setIsLoading(false);
        }
    }, [filters, selectedMerchant]);

    useEffect(() => {
        fetchData(currentPage);
    }, [fetchData, currentPage]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    useEffect(() => {
        setFilters(prev => ({ ...prev, merchantId: selectedMerchant || "" }));
        setTempFilters(prev => ({ ...prev, merchantId: selectedMerchant || "" }));
    }, [selectedMerchant]);

    useEffect(() => {
        if (filterModalOpen) {
            setTempFilters(filters);
        }
    }, [filterModalOpen, filters]);

    const toggleModal = () => {
        setIsModalOpen(!isModalOpen);
    };

    const handleViewClick = (log) => {
        setSelectedLog(log);
        toggleModal();
    };

    const handlePageChange = (page) => {
        if (page > 0 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const formatCustomDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        const day = date.getDate();
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getFullYear();
        let hours = date.getHours();
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const seconds = date.getSeconds().toString().padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12; // the hour '0' should be '12'
        return `${day} ${month} ${year}, ${hours}:${minutes}:${seconds} ${ampm}`;
    };

    const getStatusBadge = (status) => {
        if (status === 'success') {
            return <Badge color="success">Success</Badge>;
        }
        return <Badge color="danger">Failure</Badge>;
    };

    const renderPagination = () => {
        const pages = [];
        const maxPagesToShow = 5;
        const startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
        const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

        if (startPage > 1) {
            pages.push(
                <PaginationItem key="first">
                    <PaginationLink onClick={() => handlePageChange(1)}>1</PaginationLink>
                </PaginationItem>
            );
            if (startPage > 2) {
                pages.push(<PaginationItem key="start-ellipsis" disabled><PaginationLink>...</PaginationLink></PaginationItem>);
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <PaginationItem active={i === currentPage} key={i}>
                    <PaginationLink onClick={() => handlePageChange(i)}>{i}</PaginationLink>
                </PaginationItem>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                pages.push(<PaginationItem key="end-ellipsis" disabled><PaginationLink>...</PaginationLink></PaginationItem>);
            }
            pages.push(
                <PaginationItem key="last">
                    <PaginationLink onClick={() => handlePageChange(totalPages)}>{totalPages}</PaginationLink>
                </PaginationItem>
            );
        }
        return pages;
    };

    const renderAppliedFilters = () => {
        const activeFilters = Object.entries(filters).filter(([key, value]) => {
            if (key === 'limit') return false;
            if (key === 'merchantId' && userType === 'merchant') return false;
            return !!value;
        });

        if (activeFilters.length === 0) return null;

        const getFilterPill = (key, value) => {
            let label = "";
            let displayValue = value;

            switch (key) {
                // Merchant filter is now global, so we don't show it here.
                case "syncType":
                    label = "Sync Type";
                    displayValue = value.charAt(0).toUpperCase() + value.slice(1);
                    break;
                case "status":
                    label = "Status";
                    displayValue = value.charAt(0).toUpperCase() + value.slice(1);
                    break;
                case "startDate":
                    label = "Start Date";
                    displayValue = formatCustomDateTime(value);
                    break;
                case "endDate":
                    label = "End Date";
                    displayValue = formatCustomDateTime(value);
                    break;
                default:
                    return null;
            }
            return (
                <Badge color="dark" className="me-2 mb-2 p-2" key={key} style={{ backgroundColor: '#009689', fontSize: '0.8rem' }}>
                    <strong>{label}:</strong> {displayValue}
                </Badge>
            );
        };
        return <div className="mb-3">{activeFilters.map(([key, value]) => getFilterPill(key, value))}</div>;
    };

    return (
        <Fragment>
            <Breadcrumb title="Operation Sync Logs" parent="Merchants" />

            <Container fluid={true}>
                <Row className="mb-3 g-2">
                    <Col xl="3" md="6">
                        <Card className="stats-card">
                            <CardBody>
                                <div className="d-flex align-items-center">
                                    <div className="stats-icon-box bg-light-secondary">
                                        <RefreshCw className="font-secondary" />
                                    </div>
                                    <div className="ms-3">
                                        <h6 className="text-muted mb-1">Total Syncs</h6>
                                        <h4 className="mb-0 fw-bold">
                                            <CountUp end={displayStats.total} />
                                        </h4>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col xl="3" md="6">
                        <Card className="stats-card">
                            <CardBody>
                                <div className="d-flex align-items-center">
                                    <div className="stats-icon-box bg-light-primary">
                                        <CheckCircle className="font-primary" />
                                    </div>
                                    <div className="ms-3">
                                        <h6 className="text-muted mb-1">Successful</h6>
                                        <h4 className="mb-0 fw-bold">
                                            <CountUp end={displayStats.success} />
                                        </h4>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col xl="3" md="6">
                        <Card className="stats-card">
                            <CardBody>
                                <div className="d-flex align-items-center">
                                    <div className="stats-icon-box bg-light-warning">
                                        <LoaderIcon className="font-warning" />
                                    </div>
                                    <div className="ms-3">
                                        <h6 className="text-muted mb-1">Menu Syncs</h6>
                                        <h4 className="mb-0 fw-bold">
                                            <CountUp end={displayStats.menuTotal} />
                                        </h4>
                                        {/* {displayStats.menuLastSuccess && (
                                            <div className="text-muted" style={{ fontSize: '10px', marginTop: '5px' }}>
                                                Last: {formatCustomDateTime(displayStats.menuLastSuccess)}
                                            </div>
                                        )}
                                        {displayStats.menuAvgDuration && (
                                            <div className="text-muted" style={{ fontSize: '10px' }}>
                                                Avg: {Math.round(displayStats.menuAvgDuration)}ms
                                            </div>
                                        )} */}
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col xl="3" md="6">
                        <Card className="stats-card">
                            <CardBody>
                                <div className="d-flex align-items-center">
                                    <div className="stats-icon-box bg-light-info">
                                        <Clock className="font-info" />
                                    </div>
                                    <div className="ms-3">
                                        <h6 className="text-muted mb-1">Branch Syncs</h6>
                                        <h4 className="mb-0 fw-bold">
                                            <CountUp end={displayStats.branchesTotal} />
                                        </h4>
                                        {/* {displayStats.branchesLastSuccess && (
                                            <div className="text-muted" style={{ fontSize: '10px', marginTop: '5px' }}>
                                                Last: {formatCustomDateTime(displayStats.branchesLastSuccess)}
                                            </div>
                                        )}
                                        {displayStats.branchesAvgDuration && (
                                            <div className="text-muted" style={{ fontSize: '10px' }}>
                                                Avg: {Math.round(displayStats.branchesAvgDuration)}ms
                                            </div>
                                        )} */}
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                <Row>
                    <Col sm="12">
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center">
                                <h5>Operation Logs</h5>
                                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                    <Button color="primary" onClick={() => setFilterModalOpen(true)}>
                                        Filters
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardBody className="order-datatable">
                                {/* <MenuSyncLogsTable /> */}
                                {renderAppliedFilters()}
                                <div>
                                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                        <thead style={{ borderBottom: "2px solid #dee2e6" }}>
                                            <tr>
                                                <th style={tableHeaderStyle}>ID</th>
                                                <th style={tableHeaderStyle}>Date Time</th>
                                                <th style={tableHeaderStyle}>Merchant Name</th>
                                                <th style={tableHeaderStyle}>Sync Type</th>
                                                <th style={tableHeaderStyle}>Status</th>
                                                {userType === 'admin' && (
                                                    <th style={{ ...tableHeaderStyle, textAlign: "center" }}>Actions</th>
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {menuLogs?.length > 0 ? (
                                                menuLogs.map((log) => (
                                                    <tr key={log.id} style={{ borderBottom: "1px solid #dee2e6" }}>
                                                        <td style={tableCellStyle}>{log.id}</td>
                                                        <td style={tableCellStyle}>{formatCustomDateTime(log.createdAt)}</td>
                                                        <td style={tableCellStyle}>{log.merchant?.name || "N/A"}</td>
                                                        <td style={tableCellStyle}>{log.syncType}</td>
                                                        <td style={tableCellStyle}>{getStatusBadge(log.status)}</td>
                                                        {userType === 'admin' && (
                                                            <td style={tableCellStyle}>
                                                                <div style={{ display: "flex", justifyContent: "space-evenly" }}>
                                                                    <i
                                                                        className="fa fa-eye"
                                                                        style={{
                                                                            width: 35,
                                                                            fontSize: 20,
                                                                            padding: 11,
                                                                            cursor: "pointer",
                                                                            color: "#009689",
                                                                        }}
                                                                        onClick={() => handleViewClick(log)}
                                                                    ></i>
                                                                </div>
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={userType === 'admin' ? "6" : "5"} style={{ textAlign: 'center', padding: '40px' }}>
                                                        No record found
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                {menuLogs?.length > 0 && (
                                    <Pagination className="d-flex justify-content-end mt-3">
                                        <PaginationItem disabled={currentPage <= 1}>
                                            <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)} />
                                        </PaginationItem>

                                        {renderPagination()}
                                        <PaginationItem disabled={currentPage >= totalPages}>
                                            <PaginationLink next onClick={() => handlePageChange(currentPage + 1)} />
                                        </PaginationItem>
                                    </Pagination>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            <Modal isOpen={isModalOpen} toggle={toggleModal} size="lg">
                <ModalHeader toggle={toggleModal}>Log Details</ModalHeader>
                <ModalBody>
                    {selectedLog && (
                        <Container fluid>
                            <Row className="mb-3">
                                <Col md="6">
                                    <p><strong>Sync Type:</strong> {selectedLog.syncType}</p>
                                    <p><strong>Triggered By:</strong> {selectedLog.triggeredBy}</p>
                                    <p><strong>Duration:</strong> {selectedLog.durationMs} ms</p>
                                </Col>
                                <Col md="6">
                                    <p><strong>Created At:</strong> {formatCustomDateTime(selectedLog.createdAt)}</p>
                                    <p><strong>Completed At:</strong> {formatCustomDateTime(selectedLog.completedAt)}</p>
                                    <p><strong>Status:</strong> {getStatusBadge(selectedLog.status)}</p>
                                </Col>
                                {selectedLog.errorMessage && (<Col md="6">
                                    <p><strong>Error Mwssage:</strong> {selectedLog.errorMessage}</p>
                                </Col>)}
                            </Row>
                            <hr />
                            <h6 className="mb-3">Changes Summary</h6>
                            <div className="table-responsive">
                                <Table bordered hover striped>
                                    <thead className="table-light">
                                        <tr>
                                            <th>Entity</th>
                                            <th className="text-center">Created</th>
                                            <th className="text-center">Updated</th>
                                            <th className="text-center">Deactivated</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>Branches</td>
                                            <td className="text-center">{selectedLog.branchesCreated || 0}</td>
                                            <td className="text-center">{selectedLog.branchesUpdated || 0}</td>
                                            <td className="text-center">{selectedLog.branchesDeactivated || 0}</td>
                                        </tr>
                                        <tr>
                                            <td>Categories</td>
                                            <td className="text-center">{selectedLog.categoriesCreated || 0}</td>
                                            <td className="text-center">{selectedLog.categoriesUpdated || 0}</td>
                                            <td className="text-center">{selectedLog.categoriesDeactivated || 0}</td>
                                        </tr>
                                        <tr>
                                            <td>Products</td>
                                            <td className="text-center">{selectedLog.productsCreated || 0}</td>
                                            <td className="text-center">{selectedLog.productsUpdated || 0}</td>
                                            <td className="text-center">{selectedLog.productsDeactivated || 0}</td>
                                        </tr>
                                        <tr>
                                            <td>Modifiers</td>
                                            <td className="text-center">{selectedLog.modifiersCreated || 0}</td>
                                            <td className="text-center">{selectedLog.modifiersUpdated || 0}</td>
                                            <td className="text-center">-</td>
                                        </tr>
                                        <tr>
                                            <td>Schedules</td>
                                            <td className="text-center">-</td>
                                            <td className="text-center">{selectedLog.schedulesUpdated || 0}</td>
                                            <td className="text-center">-</td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </div>
                        </Container>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleModal}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Filter Modal */}
            <Modal isOpen={filterModalOpen} toggle={() => setFilterModalOpen(false)}>
                <ModalHeader toggle={() => setFilterModalOpen(false)}>Filter Logs</ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label>Show Entries</Label>
                            <Input
                                type="select"
                                value={tempFilters.limit}
                                onChange={(e) => setTempFilters({ ...tempFilters, limit: parseInt(e.target.value) })}
                            >
                                <option value="20">20</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label>Sync Type</Label>
                            <Input
                                type="select"
                                value={tempFilters.syncType}
                                onChange={(e) => setTempFilters({ ...tempFilters, syncType: e.target.value })}
                            >
                                <option value="">All</option>
                                <option value="menu">Menu</option>
                                <option value="branches">Branches</option>
                                <option value="coupons">Coupons</option>
                                <option value="google_menu">Google Menu</option>
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label>Status</Label>
                            <Input
                                type="select"
                                value={tempFilters.status}
                                onChange={(e) => setTempFilters({ ...tempFilters, status: e.target.value })}
                            >
                                <option value="">All</option>
                                <option value="success">Success</option>
                                <option value="failed">Failed</option>
                                <option value="pending">Pending</option>
                                <option value="in_progress">In Progress</option>
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label>Start Date</Label>
                            <Input
                                type="date"
                                value={tempFilters.startDate}
                                onChange={(e) => setTempFilters({ ...tempFilters, startDate: e.target.value })}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>End Date</Label>
                            <Input
                                type="date"
                                value={tempFilters.endDate}
                                onChange={(e) => setTempFilters({ ...tempFilters, endDate: e.target.value })}
                            />
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="primary"
                        onClick={() => {
                            setFilters(tempFilters);
                            setCurrentPage(1);
                            fetchData(1, tempFilters);
                            setFilterModalOpen(false);
                        }}
                    >
                        Apply Filter
                    </Button>
                    <Button
                        color="secondary"
                        onClick={() => {
                            const clearedFilters = {
                                merchantId: selectedMerchant || "",
                                syncType: "",
                                status: "",
                                startDate: "",
                                endDate: "",
                                limit: 20
                            };
                            setTempFilters(clearedFilters);
                            setFilters(clearedFilters);
                            setCurrentPage(1);
                            fetchData(1, clearedFilters);
                            setFilterModalOpen(false);
                        }}
                    >
                        Clear
                    </Button>
                </ModalFooter>
            </Modal>
        </Fragment>
    );
};

const tableHeaderStyle = {
    padding: "10px",
    textAlign: "left",
    background: "#f2f2f2",
    borderBottom: "1px solid #dee2e6",
};

const tableCellStyle = {
    padding: "10px",
    textAlign: "left",
};

export default MenuLogs;
